import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from '../store/authStore';
import '../styles/coin-shop.css';

interface ShopItem {
  id: string;
  name: string;
  description: string;
  category: string;
  price_coins: number;
  image_emoji: string;
  image_url: string | null;
}

const CAT_LABELS: Record<string, string> = {
  avatar: 'Avatar',
  theme: 'Tema',
  frame: 'Çerçeve',
  pet: 'Evcil Hayvan',
  badge: 'Rozet',
  physical: 'Fiziksel Ürün',
};

export function CoinShopPage() {
  const { profile, fetchProfile } = useAuthStore();
  const [items, setItems] = useState<ShopItem[]>([]);
  const [purchased, setPurchased] = useState<Set<string>>(new Set());
  const [coins, setCoins] = useState(0);
  const [filter, setFilter] = useState('all');
  const [msg, setMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // XP -> Coin Dönüştürücü State'leri (Orantılı: 30 XP = 1 Coin / 100 XP = 3 Coin)
  const [xpToConvert, setXpToConvert] = useState<number>(33);
  const [isConverting, setIsConverting] = useState(false);

  const userXp = profile?.xp ?? 0;
  const coinsToEarn = Math.floor((xpToConvert || 0) / 30);
  const effectiveXp = coinsToEarn * 30;
  const canConvert = userXp >= 30 && xpToConvert >= 30 && userXp >= effectiveXp && coinsToEarn >= 1;

  async function handleConvertXp() {
    if (!canConvert || isConverting || !profile) return;
    setIsConverting(true);
    try {
      const { data, error } = await supabase.rpc('convert_xp_to_coins', {
        p_student_id: profile.id,
        p_xp_amount: xpToConvert,
      });

      if (error) throw error;

      const res = data?.[0];
      if (res && res.success) {
        setCoins(res.new_coins);
        await fetchProfile(); // XP ve profil verilerini dinamik yenile
        setMsg({
          text: `🎉 ${res.xp_spent} XP harcandı ve hesabınıza +${res.coins_earned} Coin eklendi!`,
          type: 'success',
        });
        setTimeout(() => setMsg(null), 4000);
      } else {
        setMsg({ text: res?.message || 'Dönüştürme işlemi başarısız oldu.', type: 'error' });
      }
    } catch (err: any) {
      setMsg({ text: err?.message || 'XP dönüştürülürken bir hata oluştu.', type: 'error' });
    } finally {
      setIsConverting(false);
    }
  }

  // Kargo Bilgileri Modalı State'i
  const [showShipping, setShowShipping] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ShopItem | null>(null);
  const [shippingName, setShippingName] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingPhone, setShippingPhone] = useState('');
  const [shippingNotes, setShippingNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from('coin_shop_items')
      .select('*')
      .eq('is_active', true)
      .order('price_coins')
      .then(({ data }) => setItems(data ?? []));
    supabase
      .from('student_coin_purchases')
      .select('item_id')
      .eq('student_id', profile.id)
      .then(({ data }) => setPurchased(new Set((data ?? []).map((d: any) => d.item_id))));
  }, [profile?.id]);

  useEffect(() => {
    if (profile) {
      setCoins((profile as any).coins ?? 0);
    }
  }, [profile?.coins]);

  async function handleBuyClick(item: ShopItem) {
    if (coins < item.price_coins) {
      setMsg({ text: 'Yetersiz coin bakiyesi!', type: 'error' });
      return;
    }

    if (item.category === 'physical') {
      // Fiziksel ürün için kargo formu aç
      setSelectedItem(item);
      setShippingName(profile?.full_name || '');
      setShippingAddress('');
      setShippingPhone('');
      setShippingNotes('');
      setShowShipping(true);
    } else {
      // Dijital ürün için direkt satın al
      if (purchased.has(item.id)) return;
      await executePurchase(item);
    }
  }

  async function executePurchase(item: ShopItem, shippingDetails?: { name: string; address: string; phone: string; notes: string }) {
    try {
      const purchasePayload: any = {
        student_id: profile!.id,
        item_id: item.id,
      };

      if (shippingDetails) {
        purchasePayload.shipping_name = shippingDetails.name;
        purchasePayload.shipping_address = shippingDetails.address;
        purchasePayload.shipping_phone = shippingDetails.phone;
        purchasePayload.status = 'pending';
        purchasePayload.notes = shippingDetails.notes || null;
      }

      const { error } = await supabase.from('student_coin_purchases').insert(purchasePayload);
      if (error) throw error;

      await supabase.from('profiles').update({ coins: coins - item.price_coins }).eq('id', profile!.id);
      await supabase.from('coin_transactions').insert({
        student_id: profile!.id,
        amount: -item.price_coins,
        reason: `${item.name} satın alındı`,
      });

      setCoins((c) => c - item.price_coins);
      if (item.category !== 'physical') {
        setPurchased((p) => new Set([...p, item.id]));
      }

      setMsg({ text: `Satın alma işlemi başarılı: ${item.name}`, type: 'success' });
      setTimeout(() => setMsg(null), 3000);
    } catch (err) {
      setMsg({ text: 'Satın alma işlemi başarısız oldu. Lütfen tekrar deneyin.', type: 'error' });
    }
  }

  async function handleShippingSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedItem) return;
    if (!shippingName.trim() || !shippingAddress.trim() || !shippingPhone.trim()) {
      alert('Lütfen tüm zorunlu alanları doldurun.');
      return;
    }

    setIsSubmitting(true);
    await executePurchase(selectedItem, {
      name: shippingName,
      address: shippingAddress,
      phone: shippingPhone,
      notes: shippingNotes,
    });
    setIsSubmitting(false);
    setShowShipping(false);
    setSelectedItem(null);
  }

  const filtered = filter === 'all' ? items : items.filter((i) => i.category === filter);

  return (
    <div className="coin-shop-page">
      <header className="admin-page-header">
        <h1>Coin Mağazası</h1>
        <p>Kazandığınız coinler ile avatar, tema, rozet veya fiziksel ödüller alabilirsiniz.</p>
      </header>

      {/* ─── XP to Coin Takas Kartı (En Üstte) ───────────────────── */}
      <div className="xp-convert-card">
        <div className="xp-convert-header">
          <div className="xp-convert-title">
            <span>⚡ XP ile Coin Al</span>
            <span className="xp-convert-badge">1 Coin = 30 XP (100 XP ≈ 3 Coin)</span>
          </div>
          <div style={{ fontSize: 14, color: '#64748B' }}>
            Mevcut XP Bakiyeniz: <strong style={{ color: 'var(--primary, #6366f1)', fontSize: 16 }}>{userXp.toLocaleString('tr-TR')} XP</strong>
          </div>
        </div>

        {/* Orantılı Hızlı Seçenekler */}
        <div className="xp-convert-preset-grid">
          {[
            { xp: 33, coin: 1 },
            { xp: 66, coin: 2 },
            { xp: 100, coin: 3 },
            { xp: 120, coin: 4 },
            { xp: 300, coin: 10 },
          ].map((item) => {
            const disabled = userXp < item.xp;
            return (
              <button
                key={item.xp}
                type="button"
                className={`xp-convert-preset-btn ${xpToConvert === item.xp ? 'selected' : ''}`}
                disabled={disabled}
                onClick={() => setXpToConvert(item.xp)}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: disabled ? '#94a3b8' : '#334155' }}>
                  {item.xp} XP
                </span>
                <span style={{ fontSize: 13, fontWeight: 800, color: disabled ? '#94a3b8' : '#10b981' }}>
                  +{item.coin} Coin
                </span>
              </button>
            );
          })}
        </div>

        {/* Miktar Girişi ve Dönüştürme Butonu */}
        <div className="xp-convert-actions">
          <div className="xp-convert-input-group">
            <span style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>Dönüştürülecek XP:</span>
            <input
              type="number"
              step="30"
              min="30"
              max={userXp}
              value={xpToConvert || ''}
              onChange={(e) => setXpToConvert(Math.max(0, parseInt(e.target.value, 10) || 0))}
            />
            {coinsToEarn > 0 && (
              <span style={{ fontSize: 13, color: '#64748B', marginLeft: 4 }}>
                (Harcanan: <strong>{effectiveXp} XP</strong> {'->'} <strong style={{ color: '#10b981' }}>+{coinsToEarn} Coin</strong>)
              </span>
            )}
          </div>

          <button
            type="button"
            className="lesson-btn primary"
            disabled={!canConvert || isConverting}
            onClick={handleConvertXp}
            style={{
              padding: '10px 22px',
              borderRadius: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              opacity: canConvert && !isConverting ? 1 : 0.6,
              cursor: canConvert && !isConverting ? 'pointer' : 'not-allowed',
            }}
          >
            {isConverting ? 'İşleniyor...' : userXp < 30 ? '⚠️ Yetersiz XP (Min 30 XP)' : `⚡ ${effectiveXp} XP -> +${coinsToEarn} Coin Al`}
          </button>
        </div>
      </div>
      <div className="coin-shop-balance card">
        <span style={{ fontSize: 40 }}>🪙</span>
        <div>
          <div className="coin-balance-amount">{coins.toLocaleString('tr-TR')} Coin</div>
          <div className="coin-balance-hint">Ders tamamlayarak ve projeleri bitirerek coin kazanabilirsiniz.</div>
        </div>
      </div>
      {msg && (
        <div className={`quiz-error-banner ${msg.type === 'success' ? 'banner-success' : ''}`} style={{ marginBottom: 16 }}>
          {msg.text}
        </div>
      )}
      <div className="coin-shop-filters">
        {['all', 'avatar', 'theme', 'frame', 'pet', 'badge', 'physical'].map((cat) => (
          <button key={cat} className={`courses-tab ${filter === cat ? 'active' : ''}`} onClick={() => setFilter(cat)}>
            {cat === 'all' ? 'Tümü' : CAT_LABELS[cat]}
          </button>
        ))}
      </div>
      <div className="coin-shop-grid">
        {filtered.map((item) => {
          // Fiziksel ürünler birden fazla kez alınabildiği için sahipsellik kontrolü yapılmaz
          const owned = item.category !== 'physical' && purchased.has(item.id);
          const canAfford = coins >= item.price_coins;
          return (
            <div key={item.id} className={`coin-shop-item card ${owned ? 'owned' : ''}`}>
              {item.image_url ? (
                <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                  <img src={item.image_url} alt={item.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', borderRadius: 8 }} />
                </div>
              ) : (
                <div style={{ fontSize: 48, marginBottom: 8, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.image_emoji}</div>
              )}
              <div className="item-cat">{CAT_LABELS[item.category] ?? item.category}</div>
              <h4>{item.name}</h4>
              <p>{item.description}</p>
              <div className="item-footer">
                <span className="item-price">Coin: {item.price_coins}</span>
                {owned ? (
                  <span className="lesson-badge success">Sahipsiniz</span>
                ) : (
                  <button
                    className="lesson-btn primary sm"
                    onClick={() => handleBuyClick(item)}
                    disabled={!canAfford}
                    style={!canAfford ? { opacity: 0.5 } : {}}
                  >
                    {canAfford ? 'Satın Al' : 'Yetersiz'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Kargo Bilgisi Modalı */}
      {showShipping && selectedItem && (
        <div className="admin-modal-backdrop" onClick={() => !isSubmitting && setShowShipping(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Kargo Bilgileri</h3>
            <p style={{ color: 'var(--gray)', fontSize: 13, marginBottom: 16 }}>
              <strong>{selectedItem.name}</strong> fiziksel bir üründür. Lütfen kargo teslimat bilgilerinizi girin.
            </p>
            <form onSubmit={handleShippingSubmit}>
              <div className="form-group">
                <label>Alıcı Adı Soyadı</label>
                <input
                  type="text"
                  className="input"
                  value={shippingName}
                  onChange={(e) => setShippingName(e.target.value)}
                  placeholder="Kargo alıcısının adı soyadı"
                  required
                />
              </div>

              <div className="form-group">
                <label>Telefon Numarası</label>
                <input
                  type="tel"
                  className="input"
                  value={shippingPhone}
                  onChange={(e) => setShippingPhone(e.target.value)}
                  placeholder="Örn: 05551234567"
                  required
                />
              </div>

              <div className="form-group">
                <label>Kargo Adresi</label>
                <textarea
                  className="input"
                  rows={3}
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  placeholder="Mahalle, sokak, bina ve kapı no, ilçe/il tam adres"
                  required
                />
              </div>

              <div className="form-group">
                <label>Sipariş Notu (Opsiyonel)</label>
                <input
                  type="text"
                  className="input"
                  value={shippingNotes}
                  onChange={(e) => setShippingNotes(e.target.value)}
                  placeholder="Varsa eklemek istediğiniz notlar"
                />
              </div>

              <div className="admin-modal-actions" style={{ marginTop: 20 }}>
                <button type="button" className="lesson-btn secondary" onClick={() => setShowShipping(false)} disabled={isSubmitting}>
                  İptal
                </button>
                <button type="submit" className="lesson-btn primary" disabled={isSubmitting}>
                  {isSubmitting ? 'İşleniyor...' : `Siparişi Tamamla (- ${selectedItem.price_coins} Coin)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
